import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";
import type { Voucher } from "./voucherEngine";

// SPRINT V3.1 — canonical origin for all outbound voucher links.
const APP_URL = "https://versesport.com";

export function buildVoucherDeepLink(voucher: Voucher): string {
  return `${APP_URL}/?voucher=${encodeURIComponent(voucher.code)}`;
}

export function buildWhatsAppShareUrl(voucher: Voucher, lang: "ES" | "EN" | "PT" = "ES"): string {
  const meta = (voucher.metadata as { reward_name?: string } | null) ?? null;
  const rewardName = meta?.reward_name ?? voucher.code;
  const expires = voucher.expires_at ? new Date(voucher.expires_at).toLocaleDateString() : "";
  const link = buildVoucherDeepLink(voucher);
  const greet = lang === "EN" ? "I won a reward on VerseSport™!" : lang === "PT" ? "Ganhei um prêmio no VerseSport™!" : "¡Gané un premio en VerseSport™!";
  const codeLabel = lang === "EN" ? "Code" : lang === "PT" ? "Código" : "Código";
  const expLabel = lang === "EN" ? "Valid until" : lang === "PT" ? "Válido até" : "Válido hasta";
  const text =
    `${greet}\n\n🎁 ${rewardName}\n🔑 ${codeLabel}: ${voucher.code}` +
    (expires ? `\n📅 ${expLabel}: ${expires}` : "") +
    `\n\n${link}`;
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export async function shareWhatsApp(voucher: Voucher, lang: "ES" | "EN" | "PT" = "ES") {
  const url = buildWhatsAppShareUrl(voucher, lang);
  if (typeof window !== "undefined") window.open(url, "_blank", "noopener,noreferrer");
  await supabase
    .from("reward_vouchers")
    .update({ delivery_whatsapp_sent_at: new Date().toISOString() })
    .eq("id", voucher.id);
  void trackEvent("reward_voucher_whatsapp_shared", { code: voucher.code });
  void trackEvent("reward_voucher_delivered", { code: voucher.code, channel: "whatsapp" });
}

export async function downloadVoucherPdf(voucher: Voucher, lang: "ES" | "EN" | "PT" = "ES") {
  const meta = (voucher.metadata as { reward_name?: string } | null) ?? null;
  const rewardName = meta?.reward_name ?? voucher.code;
  const partnerName = voucher.partner?.name ?? "VerseSport™";
  const expires = voucher.expires_at ? new Date(voucher.expires_at).toLocaleString() : "—";
  const t = (es: string, en: string, pt: string) => (lang === "EN" ? en : lang === "PT" ? pt : es);

  const qrDataUrl = await QRCode.toDataURL(voucher.qr_payload, { errorCorrectionLevel: "M", margin: 1, scale: 8 });

  const doc = new jsPDF({ unit: "mm", format: "a6" });
  const W = doc.internal.pageSize.getWidth();

  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, W, 16, "F");
  doc.setTextColor(255);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("VERSESPORT™", W / 2, 10, { align: "center" });

  doc.setTextColor(20);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(t("VOUCHER", "VOUCHER", "VOUCHER"), W / 2, 24, { align: "center" });

  doc.setFontSize(13);
  doc.text(rewardName, W / 2, 32, { align: "center", maxWidth: W - 12 });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(90);
  doc.text(partnerName, W / 2, 38, { align: "center" });

  // QR
  const qrSize = 50;
  doc.addImage(qrDataUrl, "PNG", (W - qrSize) / 2, 42, qrSize, qrSize);

  // Code
  doc.setFontSize(13);
  doc.setFont("courier", "bold");
  doc.setTextColor(20);
  doc.text(voucher.code, W / 2, 100, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(110);
  doc.text(`${t("Válido hasta", "Valid until", "Válido até")}: ${expires}`, W / 2, 106, { align: "center" });

  doc.setFontSize(6);
  const tc = t(
    "Un solo uso. No acumulable. Sujeto a disponibilidad. Presenta este QR o código en el comercio autorizado.",
    "Single use. Non-cumulative. Subject to availability. Show this QR or code at the authorized partner.",
    "Uso único. Não acumulável. Sujeito a disponibilidade. Apresente este QR ou código no parceiro autorizado.",
  );
  doc.text(tc, 6, 114, { maxWidth: W - 12 });

  doc.save(`voucher-${voucher.code}.pdf`);

  await supabase
    .from("reward_vouchers")
    .update({ delivery_pdf_downloaded_at: new Date().toISOString() })
    .eq("id", voucher.id);
  void trackEvent("reward_voucher_pdf_downloaded", { code: voucher.code });
}
